<template>
  <div class="customer-information" :class="{ '-loading': isLoading }">
    <a-Spinner class="customer-information__spinner" v-if="isLoading" />
    <BlockContainer class="customer-information__wrapper">
      <div class="row align-items-center">
        <div class="col">
          <h5>{{ translationCustomerInformation }}</h5>
        </div>

        <div class="col edit-buttons text-right">
          <div v-if="customerInfoEditMode">
            <CancelButton class="mr-1" @click.native="customerInfoEditMode = false" />
            <SaveButton @click.native="saveAndCloseEdit" class="ml-1" />
          </div>

          <EditButton v-else @click.native="customerInfoEditMode = true" />
        </div>
      </div>

      <div v-if="localTenancyCopy.details" class="row mt-4">
        <div class="col">
          <div v-if="customerInfoEditMode">
            <hl-Input
              type="text"
              name="customerId"
              :label="translationCustomerNumber"
              v-model="localTenancyCopy.details.customClientId"
            />
          </div>
          <div v-else>
            <p class="m-0">{{ translationCustomerNumber }}</p>
            <h6>{{ localTenancyCopy.details.customClientId }}</h6>
          </div>
        </div>

        <div class="col">
          <div v-if="customerInfoEditMode">
            <hl-Input
              type="text"
              name="po-number"
              :label="translationReferenceNumber"
              v-model="localTenancyCopy.details.poNumber"
            />
          </div>
          <div v-else>
            <p class="m-0">{{ translationReferenceNumber }}</p>
            <h6>{{ localTenancyCopy.details.poNumber ? localTenancyCopy.details.poNumber : 'n/a' }}</h6>
          </div>
        </div>

        <div v-if="!customerInfoEditMode" class="col">
          <p class="m-0">{{ translationHusleieId }}</p>
          <h6>{{ localTenancyCopy.details.husleieId }}</h6>
        </div>
      </div>
    </BlockContainer>
  </div>
</template>

<script>
import BlockContainer from '@/components/BlockContainer.vue';
import Spinner from '@modules/design-system/components/atoms/Spinner.vue';
import CancelButton from '@/components/UI/buttons/CancelButton';
import EditButton from '@/components/UI/buttons/EditButton';
import SaveButton from '@/components/UI/buttons/SaveButton';
import i18nCommon from '@modules/i18n/mixins/Common';
import { mapActions } from 'vuex';

export default {
  components: {
    BlockContainer,
    CancelButton,
    EditButton,
    SaveButton,
    'a-Spinner': Spinner,
  },
  data() {
    return {
      customerInfoEditMode: false,
      loading: {
        get: false,
        post: false,
        endDate: false,
        updateBankAccount: false,
      },
      localTenancyCopy: {},
    };
  },
  mixins: [i18nCommon],
  props: {
    tenancyId: [Number, String],
    landlordId: [Number, String],
  },
  computed: {
    translationErrorCouldNotSaveTenacy() {
      return this.translate('error.could not save tenancy');
    },
    translationErrorCouldNotFetchTenacyDetails() {
      return this.translate('error.could not fetch tenancy details');
    },
    translationCustomerInformation() {
      return this.translate('customer information');
    },
    translationCustomerNumber() {
      return this.translate('tenant.customerInformation.customer number');
    },
    translationHusleieId() {
      return this.translate('tenant.customerInformation.Husleie ID');
    },
    translationReferenceNumber() {
      return this.translate('tenant.customerInformation.reference number');
    },
    isLoading() {
      return this.loading.post;
    },
  },
  methods: {
    ...mapActions('tenancy', ['updateTenancy', 'fetchTenancy']),
    async saveAndCloseEdit() {
      await this.saveTenancyDetails();
      this.customerInfoEditMode = false;
    },
    async fetchTenancyDetails() {
      try {
        this.localTenancyCopy = await this.fetchTenancy({
          landlordId: this.landlordId,
          tenancyId: this.tenancyId,
        });
      } catch (error) {
        console.error(error);
        this.$toast(this.translationErrorCouldNotFetchTenacyDetails, 'error');
      }
    },
    async saveTenancyDetails() {
      this.loading.post = true;
      const tenancy = JSON.parse(JSON.stringify(this.localTenancyCopy));

      // delete readOnly keys from tenancy Object
      delete tenancy.id;
      delete tenancy.rentalObjectId;
      delete tenancy.hasDeposits;
      delete tenancy.rent;
      delete tenancy.tenants;
      delete tenancy.subProducts;
      delete tenancy.rentAdjustment;
      delete tenancy.subscription;
      try {
        await this.updateTenancy({
          landlordId: this.landlordId,
          tenancyId: this.tenancyId,
          data: tenancy,
        });
        this.loading.post = false;
      } catch (error) {
        console.error(error);
        this.$toast(this.translationErrorCouldNotSaveTenacy, 'error');
      }
    },
  },
  mounted() {
    this.fetchTenancyDetails();
  },
};
</script>
<style>
.customer-information.-loading .customer-information__spinner {
  @apply block absolute left-[calc(50%-18px)] top-[calc(50%-13px)];
}

.customer-information {
  @apply relative;
}

.customer-information .customer-information__wrapper {
  @apply p-5;
}

.customer-information.-loading .customer-information__wrapper {
  @apply opacity-20;
}
</style>
